/**
 * What the creator gets back for granting us access.
 *
 * Every figure here is measured — read from their own YouTube Analytics or
 * Instagram account, not modelled. If something is missing it is because we
 * could not read it, never because we estimated it. That distinction is the
 * whole point of the page, so it is stated on the page rather than assumed.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getInsightsStatus, getMyAudience } from '../../../api_handlers/insights';
import {
  Alert,
  Empty,
  ICONS,
  Icon,
  KIT_CSS,
  useResponsiveViewport,
  useStyleSheet,
} from '../../invoices/kit';

const CSS = `
${KIT_CSS}

.au-page { max-width: 1080px; margin: 0 auto; padding: 28px 20px 72px; }

.au-head { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 14px 24px; margin-bottom: 8px; }
.au-head__title { font-size: 27px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.au-head__lede { font-size: 14.5px; color: var(--ink-2); max-width: 62ch; margin-top: 6px; }

.au-measured {
  display: inline-flex; align-items: center; gap: 6px; border-radius: 999px;
  padding: 4px 11px 4px 9px; font-size: 11.5px; font-weight: 800; white-space: nowrap;
  background: #E7F6F0; color: #0A6B5C; border: 1px solid #BFE5D9;
}

.au-grid { display: grid; gap: 18px; margin-top: 22px; }
@media (min-width: 900px) { .au-grid { grid-template-columns: 1fr 1fr; } }

.au-card {
  background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius);
  padding: 20px 22px 22px; min-width: 0;
}
.au-card--wide { grid-column: 1 / -1; }

.au-card__head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 4px; }
.au-card__title { font-size: 15px; font-weight: 800; color: var(--ink); letter-spacing: -.01em; }
.au-card__note { font-size: 11.5px; color: var(--ink-3); font-variant-numeric: tabular-nums; white-space: nowrap; }
.au-card__sub { font-size: 12.5px; color: var(--ink-3); margin-bottom: 16px; }

.au-bars { display: flex; flex-direction: column; gap: 11px; }
.au-bar { display: grid; grid-template-columns: 62px 1fr 48px; align-items: center; gap: 12px; }
.au-bar__label { font-size: 12.5px; font-weight: 700; color: var(--ink-2); }
.au-bar__track { height: 9px; border-radius: 999px; background: var(--line); overflow: hidden; }
.au-bar__fill { height: 100%; border-radius: 999px; background: var(--accent); }
.au-bar__fill--alt { background: #0A6B5C; }
.au-bar__value { font-size: 12.5px; font-weight: 700; color: var(--ink); text-align: right; font-variant-numeric: tabular-nums; }

.au-stat { display: flex; flex-wrap: wrap; gap: 26px; }
.au-stat__item { min-width: 96px; }
.au-stat__n { font-size: 26px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); font-variant-numeric: tabular-nums; }
.au-stat__n--up { color: #0A6B5C; }
.au-stat__n--down { color: #B4271C; }
.au-stat__k { font-size: 11.5px; color: var(--ink-3); font-weight: 700; margin-top: 2px; }

.au-list { display: flex; flex-direction: column; gap: 9px; }
.au-list__row { display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center; }
.au-list__name { font-size: 13px; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.au-list__pct { font-size: 12.5px; font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }

.au-banner {
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px 18px;
  border: 1px solid #F0C9A8; background: #FDF6EF; border-radius: var(--radius);
  padding: 14px 18px; margin-bottom: 20px;
}
.au-banner__icon { color: #A85F1B; display: flex; flex: none; }
.au-banner__body { flex: 1 1 300px; min-width: 0; }
.au-banner__title { font-size: 13.5px; font-weight: 800; color: #7A430F; }
.au-banner__text { font-size: 12.5px; color: #8A5A2B; margin-top: 3px; }
.au-banner__cta {
  font-size: 12.5px; font-weight: 800; color: #fff; background: #A85F1B;
  border: 0; border-radius: var(--radius-sm); padding: 9px 15px; cursor: pointer;
  text-decoration: none; display: inline-flex; align-items: center; gap: 6px;
}
.au-banner__cta:hover { background: #8A4D14; }

.au-disclosure {
  margin-top: 34px; border-top: 1px solid var(--line); padding-top: 22px;
}
.au-disclosure__title { font-size: 14px; font-weight: 800; color: var(--ink); margin-bottom: 10px; }
.au-disclosure__cols { display: grid; gap: 20px 34px; }
@media (min-width: 760px) { .au-disclosure__cols { grid-template-columns: 1fr 1fr; } }
.au-disclosure__h { font-size: 12px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-3); margin-bottom: 8px; }
.au-disclosure__list { display: flex; flex-direction: column; gap: 6px; }
.au-disclosure__item { font-size: 13px; color: var(--ink-2); display: flex; gap: 8px; align-items: flex-start; }
.au-disclosure__mark { flex: none; margin-top: 2px; color: #0A6B5C; display: flex; }
.au-disclosure__mark--no { color: var(--ink-3); }
.au-disclosure__foot { font-size: 12.5px; color: var(--ink-3); margin-top: 18px; max-width: 70ch; }
`;

/**
 * We are asking creators for analytics access. The disclosure should be
 * better than the legal minimum, and it belongs where they are actually
 * looking at the data rather than buried in a policy page.
 */
const WE_READ = [
  'Your audience age, gender and location — as percentages, never individual people',
  'Views, watch time and engagement on your posts',
  'Subscriber and follower movement over time',
  'Where your views come from (search, feed, shares)',
];

const WE_DO_NOT = [
  'Your private messages, comments or contacts',
  'Your revenue, RPM or any payout figure',
  'Anything that identifies an individual follower',
  'Permission to post, edit or delete on your behalf',
];

function Disclosure() {
  return (
    <div className="au-disclosure">
      <p className="au-disclosure__title">What we read from your accounts</p>
      <div className="au-disclosure__cols">
        <div>
          <p className="au-disclosure__h">We read</p>
          <div className="au-disclosure__list">
            {WE_READ.map((line) => (
              <span className="au-disclosure__item" key={line}>
                <span className="au-disclosure__mark">
                  <Icon path={ICONS.check} size={13} />
                </span>
                {line}
              </span>
            ))}
          </div>
        </div>
        <div>
          <p className="au-disclosure__h">We never read</p>
          <div className="au-disclosure__list">
            {WE_DO_NOT.map((line) => (
              <span className="au-disclosure__item" key={line}>
                <span className="au-disclosure__mark au-disclosure__mark--no">
                  <Icon path={ICONS.x} size={13} />
                </span>
                {line}
              </span>
            ))}
          </div>
        </div>
      </div>
      <p className="au-disclosure__foot">
        Brands see your audience breakdown and post performance — the same figures on this page.
        You can disconnect either account at any time from{' '}
        <Link to="/influencer/settings/social-settings">social settings</Link>, and we stop
        collecting immediately.
      </p>
    </div>
  );
}

const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const GENDER_LABELS = { male: 'Men', female: 'Women', M: 'Men', F: 'Women', U: 'Other', other: 'Other' };

function pct(value, total) {
  if (!total) return 0;
  return (value / total) * 100;
}

function sorted(map, order) {
  const keys = Object.keys(map || {});
  if (!order) return keys.sort((a, b) => map[b] - map[a]);
  return keys.sort((a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function BarList({ data, order, alt }) {
  const total = Object.values(data || {}).reduce((sum, n) => sum + n, 0);
  const keys = sorted(data, order);
  if (!keys.length) return null;
  const peak = Math.max(...keys.map((k) => data[k]));
  return (
    <div className="au-bars">
      {keys.map((key) => {
        const share = pct(data[key], total);
        return (
          <div className="au-bar" key={key}>
            <span className="au-bar__label">{GENDER_LABELS[key] || key}</span>
            <span className="au-bar__track">
              <span
                className={`au-bar__fill${alt ? ' au-bar__fill--alt' : ''}`}
                style={{ width: `${peak ? (data[key] / peak) * 100 : 0}%` }}
              />
            </span>
            <span className="au-bar__value">{share.toFixed(1)}%</span>
          </div>
        );
      })}
    </div>
  );
}

function Places({ title, sub, rows, nameKey }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="au-card">
      <div className="au-card__head">
        <span className="au-card__title">{title}</span>
      </div>
      <p className="au-card__sub">{sub}</p>
      <div className="au-list">
        {rows.map((row) => (
          <div className="au-list__row" key={row[nameKey] || row.code || row.value}>
            <span className="au-list__name">{row[nameKey] || row.code || row.value}</span>
            <span className="au-list__pct">{(row.weight * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Shown wherever a creator is, not only on this page: a declined permission
 * or a revoked token is invisible to them otherwise, and the only symptom is
 * a page that never fills in.
 */
export function ReconnectBanner() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    getInsightsStatus().then(
      (data) => {
        if (active) setStatus(data);
      },
      () => {},
    );
    return () => {
      active = false;
    };
  }, []);

  if (!status || !status.needs_reconnect) return null;

  const platform = status.youtube && status.youtube.needs_reconnect ? 'YouTube' : 'Instagram';
  const revoked =
    (status.youtube && status.youtube.status === 'token_dead') ||
    (status.instagram && status.instagram.status === 'token_dead');

  return (
    <div className="au-banner">
      <span className="au-banner__icon">
        <Icon path={ICONS.alert} size={20} />
      </span>
      <div className="au-banner__body">
        <div className="au-banner__title">Reconnect {platform} to show brands your real audience</div>
        <p className="au-banner__text">
          {revoked
            ? `Your ${platform} connection has expired, so we can no longer read your own numbers.`
            : `We do not have permission to read your ${platform} audience data, so brands are seeing an estimate instead of your real figures.`}{' '}
          Reconnecting takes one tap.
        </p>
      </div>
      <Link className="au-banner__cta" to="/influencer/settings/social-settings">
        Reconnect
        <Icon path={ICONS.arrow} size={14} />
      </Link>
    </div>
  );
}

function PlatformBlock({ name, data }) {
  if (!data) return null;
  const hasAges = Object.keys(data.ages || {}).length > 0;
  const hasGenders = Object.keys(data.genders || {}).length > 0;
  const subs = data.subscribers;

  return (
    <>
      {hasAges && (
        <div className="au-card">
          <div className="au-card__head">
            <span className="au-card__title">{name} · age</span>
            <span className="au-card__note">
              {data.period_start} — {data.period_end}
            </span>
          </div>
          <p className="au-card__sub">Share of your audience in each age band.</p>
          <BarList data={data.ages} order={AGE_ORDER} />
        </div>
      )}

      {hasGenders && (
        <div className="au-card">
          <div className="au-card__head">
            <span className="au-card__title">{name} · gender</span>
          </div>
          <p className="au-card__sub">How your audience splits.</p>
          <BarList data={data.genders} alt />
        </div>
      )}

      {subs && (
        <div className="au-card">
          <div className="au-card__head">
            <span className="au-card__title">{name} · subscribers</span>
            <span className="au-card__note">
              {subs.period_start} — {subs.period_end}
            </span>
          </div>
          <p className="au-card__sub">Movement over the period we have data for.</p>
          <div className="au-stat">
            <div className="au-stat__item">
              <div className={`au-stat__n${subs.net >= 0 ? ' au-stat__n--up' : ' au-stat__n--down'}`}>
                {subs.net >= 0 ? '+' : ''}
                {subs.net.toLocaleString()}
              </div>
              <div className="au-stat__k">Net</div>
            </div>
            <div className="au-stat__item">
              <div className="au-stat__n">{subs.gained.toLocaleString()}</div>
              <div className="au-stat__k">Gained</div>
            </div>
            <div className="au-stat__item">
              <div className="au-stat__n">{subs.lost.toLocaleString()}</div>
              <div className="au-stat__k">Lost</div>
            </div>
          </div>
        </div>
      )}

      <Places
        title={`${name} · top countries`}
        sub="Where your audience watches from."
        rows={(data.countries || []).slice(0, 8)}
        nameKey="code"
      />
      <Places
        title={`${name} · top cities`}
        sub="Your densest local audiences."
        rows={(data.cities || []).slice(0, 8)}
        nameKey="value"
      />
    </>
  );
}

export default function AudiencePanel() {
  useStyleSheet('au-kit', CSS);
  useResponsiveViewport();

  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    getMyAudience().then(
      (data) => setState({ loading: false, error: null, data }),
      (error) => setState({ loading: false, error, data: null }),
    );
  }, []);

  useEffect(load, [load]);

  const data = state.data || {};
  const nothingYet = !state.loading && !state.error && !data.youtube && !data.instagram;

  return (
    <div className="iv-root">
      <div className="au-page">
        <ReconnectBanner />

        <div className="au-head">
          <div>
            <h1 className="au-head__title">Your audience</h1>
            <p className="au-head__lede">
              Read straight from your own YouTube and Instagram accounts — not estimated, not
              bought from anyone. This is what brands see about you.
            </p>
          </div>
          <span className="au-measured">
            <Icon path={ICONS.check} size={13} />
            Measured
          </span>
        </div>

        {state.error && <Alert onRetry={load}>We could not load your audience just now.</Alert>}

        {nothingYet && (
          <Empty icon={ICONS.eye} title="Nothing to show yet">
            We collect your numbers on a schedule, so a freshly connected account takes a day to
            appear. If you have just reconnected, check back tomorrow.
          </Empty>
        )}

        {!state.loading && !nothingYet && (
          <div className="au-grid">
            <PlatformBlock name="YouTube" data={data.youtube} />
            <PlatformBlock name="Instagram" data={data.instagram} />
          </div>
        )}

        <Disclosure />
      </div>
    </div>
  );
}
